import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { BookCheck, ClipboardCheck, Check, ChevronDown, Info, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ZIYADAH_HINT, formatHalaman, halamanToInput } from '@/utils/halaman';
import EditSetoranModal from './EditSetoranModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const SURAHS = [
  [1,"Al-Fatihah"],[2,"Al-Baqarah"],[3,"Ali 'Imran"],[4,"An-Nisa'"],[5,"Al-Ma'idah"],
  [6,"Al-An'am"],[7,"Al-A'raf"],[8,"Al-Anfal"],[9,"At-Taubah"],[10,"Yunus"],
  [11,"Hud"],[12,"Yusuf"],[13,"Ar-Ra'd"],[14,"Ibrahim"],[15,"Al-Hijr"],
  [16,"An-Nahl"],[17,"Al-Isra'"],[18,"Al-Kahf"],[19,"Maryam"],[20,"Ta Ha"],
  [21,"Al-Anbiya'"],[22,"Al-Hajj"],[23,"Al-Mu'minun"],[24,"An-Nur"],[25,"Al-Furqan"],
  [26,"Asy-Syu'ara'"],[27,"An-Naml"],[28,"Al-Qasas"],[29,"Al-'Ankabut"],[30,"Ar-Rum"],
  [31,"Luqman"],[32,"As-Sajdah"],[33,"Al-Ahzab"],[34,"Saba'"],[35,"Fatir"],
  [36,"Ya Sin"],[37,"As-Saffat"],[38,"Sad"],[39,"Az-Zumar"],[40,"Gafir"],
  [41,"Fussilat"],[42,"Asy-Syura"],[43,"Az-Zukhruf"],[44,"Ad-Dukhan"],[45,"Al-Jasiyah"],
  [46,"Al-Ahqaf"],[47,"Muhammad"],[48,"Al-Fath"],[49,"Al-Hujurat"],[50,"Qaf"],
  [51,"Az-Zariyat"],[52,"At-Tur"],[53,"An-Najm"],[54,"Al-Qamar"],[55,"Ar-Rahman"],
  [56,"Al-Waqi'ah"],[57,"Al-Hadid"],[58,"Al-Mujadilah"],[59,"Al-Hasyr"],[60,"Al-Mumtahanah"],
  [61,"As-Saff"],[62,"Al-Jumu'ah"],[63,"Al-Munafiqun"],[64,"At-Tagabun"],[65,"At-Talaq"],
  [66,"At-Tahrim"],[67,"Al-Mulk"],[68,"Al-Qalam"],[69,"Al-Haqqah"],[70,"Al-Ma'arij"],
  [71,"Nuh"],[72,"Al-Jinn"],[73,"Al-Muzammil"],[74,"Al-Muddassir"],[75,"Al-Qiyamah"],
  [76,"Al-Insan"],[77,"Al-Mursalat"],[78,"An-Naba'"],[79,"An-Nazi'at"],[80,"'Abasa"],
  [81,"At-Takwir"],[82,"Al-Infitar"],[83,"Al-Mutaffifin"],[84,"Al-Insyiqaq"],[85,"Al-Buruj"],
  [86,"At-Tariq"],[87,"Al-A'la"],[88,"Al-Gasiyah"],[89,"Al-Fajr"],[90,"Al-Balad"],
  [91,"Asy-Syams"],[92,"Al-Lail"],[93,"Ad-Duha"],[94,"Al-Insyirah"],[95,"At-Tin"],
  [96,"Al-'Alaq"],[97,"Al-Qadr"],[98,"Al-Bayyinah"],[99,"Az-Zalzalah"],[100,"Al-'Adiyat"],
  [101,"Al-Qari'ah"],[102,"At-Takasur"],[103,"Al-'Asr"],[104,"Al-Humazah"],[105,"Al-Fil"],
  [106,"Quraisy"],[107,"Al-Ma'un"],[108,"Al-Kausar"],[109,"Al-Kafirun"],[110,"An-Nasr"],
  [111,"Al-Masad"],[112,"Al-Ikhlas"],[113,"Al-Falaq"],[114,"An-Nas"]
];

// Kelas config
export const KELAS_CONFIG = {
  D: { label: "I'dad", color: 'bg-gray-100 text-gray-600', ring: 'ring-gray-300' },
  C: { label: 'Uula', color: 'bg-yellow-100 text-yellow-700', ring: 'ring-yellow-300' },
  B: { label: 'Wustha', color: 'bg-blue-100 text-blue-700', ring: 'ring-blue-300' },
  A: { label: 'Ulya', color: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-300' },
  M: { label: 'Ali/Mahir', color: 'bg-purple-100 text-purple-700', ring: 'ring-purple-300' },
};

const JUZ_LIST = Array.from({ length: 30 }, (_, i) => i + 1);
// Ziyadah options: 1/4, 1/3, 1/2, then for each integer N: N, N+1/4, N+1/3, N+1/2 (no 3/4 anywhere)
const ZIYADAH_OPTS = (() => {
  const opts = [
    { value: 0.25, label: '1/4' },
    { value: 1/3,  label: '1/3' },
    { value: 0.5,  label: '1/2' },
  ];
  for (let n = 1; n <= 10; n++) {
    opts.push({ value: n,      label: halamanToInput(n)      });
    opts.push({ value: n+0.25, label: halamanToInput(n+0.25) });
    opts.push({ value: n+1/3,  label: halamanToInput(n+1/3)  });
    opts.push({ value: n+0.5,  label: halamanToInput(n+0.5)  });
  }
  return opts;
})();

const initRange = () => ({ juz: 1, dari_halaman: '', sampai_halaman: '', jml_halaman: '' });
const initZiyadah = () => ({ juz: 1, dari_halaman: '', sampai_halaman: '', jml_halaman: 1 });

const emptyAbsensi = {
  santri_id: '', halaqah_id: '', tanggal: new Date().toISOString().split('T')[0], catatan: '',
};

const STATUS_OPTS = [
  { code: 'H', label: 'Hadir', on: 'bg-emerald-500 text-white border-emerald-500' },
  { code: 'S', label: 'Sakit', on: 'bg-blue-500 text-white border-blue-500' },
  { code: 'I', label: 'Izin', on: 'bg-amber-500 text-white border-amber-500' },
  { code: 'A', label: 'Alpa', on: 'bg-red-500 text-white border-red-500' },
  { code: 'TS', label: 'Tidak Setor', on: 'bg-gray-700 text-white border-gray-700' },
];

// ─── Toggle Component (rapi di semua ukuran layar) ──────────
export function Toggle({ active, onChange, testId }) {
  return (
    <button type="button" data-testid={testId} role="switch" aria-checked={active}
      onClick={() => onChange(!active)}
      className={`relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-300 ease-in-out focus:outline-none ${active ? 'bg-emerald-500' : 'bg-gray-300'}`}
      style={{ width: 48, height: 28, padding: 2 }}>
      <span className="bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out"
        style={{ width: 24, height: 24, transform: active ? 'translateX(20px)' : 'translateX(0px)' }} />
    </button>
  );
}

// ─── Juz + Halaman Input ────────────────────────────────────
// mode='ziyadah' → jumlah setoran pakai dropdown kelipatan 1/4
// mode='range'   → jumlah halaman otomatis dari selisih, bisa diedit manual
function JuzHalamanInput({ data, onChange, mode, testId }) {
  const setField = (patch) => {
    const next = { ...data, ...patch };
    // Auto hitung jumlah halaman untuk mode range
    if (mode === 'range' && ('dari_halaman' in patch || 'sampai_halaman' in patch)) {
      const d = parseInt(next.dari_halaman);
      const s = parseInt(next.sampai_halaman);
      if (!isNaN(d) && !isNaN(s) && s >= d) next.jml_halaman = String(s - d + 1);
    }
    onChange(next);
  };
  return (
    <div className="space-y-2.5 pt-3">
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Juz</label>
        <div className="relative">
          <select value={data.juz} onChange={e => setField({ juz: parseInt(e.target.value) })}
            data-testid={`${testId}-juz`}
            className="w-full pl-3 pr-7 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
            {JUZ_LIST.map(j => <option key={j} value={j}>Juz {j}</option>)}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Dari Halaman</label>
          <input type="number" min={1} inputMode="numeric" value={data.dari_halaman}
            onChange={e => setField({ dari_halaman: e.target.value })}
            data-testid={`${testId}-dari`} placeholder="0"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Sampai Halaman</label>
          <input type="number" min={1} inputMode="numeric" value={data.sampai_halaman}
            onChange={e => setField({ sampai_halaman: e.target.value })}
            data-testid={`${testId}-sampai`} placeholder="0"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
        </div>
      </div>

      {mode === 'ziyadah' ? (
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Jumlah Setoran</label>
          <div className="relative">
            <select value={data.jml_halaman} onChange={e => setField({ jml_halaman: Number(e.target.value) })}
              data-testid={`${testId}-jml`}
              className="w-full pl-3 pr-7 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
              {ZIYADAH_OPTS.map(o => <option key={o.value} value={o.value}>{o.label} halaman</option>)}
            </select>
            <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <p className="text-[10px] leading-snug text-gray-400 flex items-start gap-1 mt-1.5" data-testid={`${testId}-hint`}>
            <Info size={11} className="flex-shrink-0 mt-0.5 text-emerald-500" />
            <span>{ZIYADAH_HINT}</span>
          </p>
        </div>
      ) : (
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Jumlah Halaman</label>
          <input type="number" min={0} step="0.25" inputMode="decimal" value={data.jml_halaman}
            onChange={e => setField({ jml_halaman: e.target.value })}
            data-testid={`${testId}-jml`} placeholder="0"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
          <p className="text-[10px] leading-snug text-gray-400 flex items-start gap-1 mt-1.5">
            <Info size={11} className="flex-shrink-0 mt-0.5 text-emerald-500" />
            <span>Otomatis terhitung dari selisih halaman, bisa diubah manual.</span>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Session block untuk absensi ────────────────────────────
function SesiBlock({ title, value, onChange, testIdPrefix }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-emerald-800">{title}</span>
        {value && (
          <button type="button" onClick={() => onChange('')} data-testid={`${testIdPrefix}-clear`}
            className="text-[10px] text-gray-400 font-medium hover:text-red-500">Kosongkan</button>
        )}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {STATUS_OPTS.map(o => (
          <button key={o.code} type="button" data-testid={`${testIdPrefix}-${o.code}`}
            onClick={() => onChange(value === o.code ? '' : o.code)}
            className={`py-2 rounded-xl text-[11px] font-bold border transition-all duration-200 ${
              value === o.code ? o.on : 'bg-white text-gray-500 border-gray-200'
            }`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────
export default function InputSetoran() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'setoran');
  const [santri, setSantri] = useState([]);
  const [halaqah, setHalaqah] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingA, setLoadingA] = useState(false);

  // Shared data santri
  const [santriId, setSantriId] = useState('');
  const [halaqahId, setHalaqahId] = useState('');
  const [tanggal, setTanggal] = useState(new Date().toISOString().split('T')[0]);
  const [santriKelas, setSantriKelas] = useState('');

  // Setoran sections (for non-I'dad)
  const [ziyadahActive, setZiyadahActive] = useState(true);
  const [ziyadah, setZiyadah] = useState(initZiyadah());
  const [murajaahActive, setMurajaahActive] = useState(false);
  const [murajaah, setMurajaah] = useState(initRange());
  const [binnazharActive, setBinnazharActive] = useState(false);
  const [binnazhar, setBinnazhar] = useState(initRange());

  // Tasmi' (kelas C dan atas)
  const [tasmiActive, setTasmiActive] = useState(false);
  const [jumlahJuz, setJumlahJuz] = useState(1);
  const [tasmiStatus, setTasmiStatus] = useState('Lulus');

  // Iqro (kelas D only)
  const [iqroJilid, setIqroJilid] = useState(1);
  const [iqroHalaman, setIqroHalaman] = useState(1);

  const [catatan, setCatatan] = useState('');

  // Absensi
  const [formA, setFormA] = useState(emptyAbsensi);
  const [sesiSore, setSesiSore] = useState('');
  const [sesiMalam, setSesiMalam] = useState('');

  // Setoran history (shown below form)
  const [recentSetoran, setRecentSetoran] = useState([]);
  const [allSetoran, setAllSetoran] = useState([]);
  const [riwayatTab, setRiwayatTab] = useState('ziyadah');
  const [editingSetoran, setEditingSetoran] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const JENIS_LABEL_MAP = { ziyadah: 'Ziyadah', murajaah: 'Murajaah', bin_nadzar: 'Binnadzar', bin_nadhar: 'Binnadzar', tasmi: "Tasmi'", iqro: 'Iqro' };
  const JENIS_COLOR_MAP = { ziyadah: 'bg-emerald-100 text-emerald-700', murajaah: 'bg-blue-100 text-blue-700', bin_nadzar: 'bg-purple-100 text-purple-700', bin_nadhar: 'bg-purple-100 text-purple-700', tasmi: 'bg-amber-100 text-amber-700', iqro: 'bg-orange-100 text-orange-700' };

  const isIdad = santriKelas === 'D';
  const isTasmiEligible = ['C', 'B', 'A', 'M'].includes(santriKelas);

  const loadRecentSetoran = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/setoran`);
      setRecentSetoran(r.data.slice(0, 10));
      setAllSetoran(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/santri`).then(r => setSantri(r.data)).catch(() => {}),
      axios.get(`${API}/halaqah`).then(r => setHalaqah(r.data)).catch(() => {}),
      loadRecentSetoran(),
    ]);
  }, [loadRecentSetoran]);

  // Update kelas & halaqah when santri changes
  useEffect(() => {
    if (santriId) {
      const s = santri.find(s => s.id === santriId);
      setSantriKelas(s?.kelas || '');
      if (s?.halaqah_id && halaqah.some(h => h.id === s.halaqah_id)) setHalaqahId(s.halaqah_id);
    } else {
      setSantriKelas('');
    }
  }, [santriId, santri, halaqah]);

  const basePayload = useCallback((jenis, extraFields = {}) => ({
    santri_id: santriId,
    halaqah_id: halaqahId || undefined,
    tanggal,
    jenis,
    catatan,
    ...extraFields,
  }), [santriId, halaqahId, tanggal, catatan]);

  const resetForm = useCallback(() => {
    setSantriId(''); setHalaqahId('');
    setTanggal(new Date().toISOString().split('T')[0]);
    setSantriKelas('');
    setZiyadahActive(true); setZiyadah(initZiyadah());
    setMurajaahActive(false); setMurajaah(initRange());
    setBinnazharActive(false); setBinnazhar(initRange());
    setTasmiActive(false); setJumlahJuz(1); setTasmiStatus('Lulus');
    setIqroJilid(1); setIqroHalaman(1);
    setCatatan('');
  }, []);

  // Validasi + rakit payload rentang Juz/halaman
  const buildRange = (r, label, isZiyadah) => {
    const juz = parseInt(r.juz) || 0;
    if (juz < 1) { toast.error(`Pilih Juz untuk ${label}`); return null; }
    const jml = Number(r.jml_halaman) || 0;
    if (jml <= 0) {
      toast.error(isZiyadah ? `Pilih jumlah setoran ${label}` : `Isi jumlah halaman ${label} lebih dari 0`);
      return null;
    }
    const dari = parseInt(r.dari_halaman);
    const sampai = parseInt(r.sampai_halaman);
    return {
      juz,
      dari_halaman: isNaN(dari) ? null : dari,
      sampai_halaman: isNaN(sampai) ? null : sampai,
      jml_halaman: jml,
    };
  };

  const submitSetoran = async (e) => {
    e.preventDefault();
    if (!santriId) { toast.error('Pilih santri terlebih dahulu'); return; }

    const toSubmit = [];

    // Iqro – hanya kelas D, selalu disertakan
    if (isIdad) {
      toSubmit.push(basePayload('iqro', { iqro_jilid: iqroJilid, iqro_halaman: iqroHalaman, jml_halaman: 0 }));
    }

    // Ziyadah – semua kelas termasuk D
    if (ziyadahActive) {
      const z = buildRange(ziyadah, 'Ziyadah', true); if (!z) return;
      toSubmit.push(basePayload('ziyadah', z));
    }

    // Murajaah – semua kelas termasuk D
    if (murajaahActive) {
      const m = buildRange(murajaah, 'Murajaah', false); if (!m) return;
      toSubmit.push(basePayload('murajaah', m));
    }

    // Binnazhar – hanya non-D
    if (!isIdad && binnazharActive) {
      const b = buildRange(binnazhar, 'Bin Nadzar', false); if (!b) return;
      toSubmit.push(basePayload('bin_nadhar', b));
    }

    // Tasmi' – hanya kelas C ke atas
    if (tasmiActive && isTasmiEligible) {
      toSubmit.push(basePayload('tasmi', { jumlah_juz: jumlahJuz, tasmi_status: tasmiStatus, jml_halaman: 0 }));
    }

    if (toSubmit.length === 0) {
      toast.error('Aktifkan minimal satu jenis setoran'); return;
    }

    setLoading(true);
    try {
      await Promise.all(toSubmit.map(p => axios.post(`${API}/setoran`, p)));
      toast.success(`${toSubmit.length} setoran berhasil disimpan!`);
      resetForm();
      loadRecentSetoran();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan setoran');
    } finally { setLoading(false); }
  };

  const handleDeleteSetoran = async (id) => {
    try {
      await axios.delete(`${API}/setoran/${id}`);
      setRecentSetoran(prev => prev.filter(s => s.id !== id));
      setAllSetoran(prev => prev.filter(s => s.id !== id));
      setConfirmDeleteId(null);
      toast.success('Setoran berhasil dihapus');
    } catch { toast.error('Gagal menghapus setoran'); }
  };

  const submitAbsensi = async (e) => {
    e.preventDefault();
    if (!formA.santri_id) { toast.error('Pilih santri terlebih dahulu'); return; }
    const toSave = [];
    if (sesiSore) toSave.push({ ...formA, halaqah_id: formA.halaqah_id || undefined, status: sesiSore, sesi: 'sore' });
    if (sesiMalam) toSave.push({ ...formA, halaqah_id: formA.halaqah_id || undefined, status: sesiMalam, sesi: 'malam' });
    if (toSave.length === 0) { toast.error('Isi minimal satu sesi (Sore atau Malam)'); return; }

    setLoadingA(true);
    try {
      await Promise.all(toSave.map(p => axios.post(`${API}/absensi`, p)));
      toast.success(`Absensi ${toSave.length} sesi berhasil disimpan!`);
      setFormA(emptyAbsensi); setSesiSore(''); setSesiMalam('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
    finally { setLoadingA(false); }
  };

  const selectedSantri = santri.find(s => s.id === santriId);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-emerald-900 border-b border-emerald-800 px-6 pt-12 pb-4 sticky top-0 z-10">
          <h1 className="font-playfair text-2xl font-bold text-white mb-4">Input Data</h1>
          <div className="flex gap-1 bg-emerald-800/60 rounded-xl p-1">
            {[['setoran', 'Setoran Hafalan', BookCheck], ['absensi', 'Absensi', ClipboardCheck]].map(([key, label, Icon]) => (
              <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  tab === key ? 'bg-white text-emerald-700 shadow-sm' : 'text-emerald-200'
                }`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 pb-32 md:pb-8 space-y-4">
          {tab === 'setoran' && (<>
            <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onSubmit={submitSetoran} data-testid="setoran-form">

              {/* ── Data Santri ── */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Data Santri</h3>
                <select required value={santriId} onChange={e => setSantriId(e.target.value)}
                  data-testid="setoran-santri-select"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 mb-2.5">
                  <option value="">-- Pilih Santri --</option>
                  {santri.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.nis}){s.kelas ? ` · Kelas ${s.kelas}` : ''}
                    </option>
                  ))}
                </select>

                {selectedSantri?.kelas && (
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xs text-gray-500">Kelas:</span>
                    <span data-testid="santri-kelas-badge"
                      className={`text-xs font-bold px-2.5 py-1 rounded-xl ${KELAS_CONFIG[selectedSantri.kelas]?.color || 'bg-gray-100 text-gray-600'}`}>
                      {selectedSantri.kelas} - {KELAS_CONFIG[selectedSantri.kelas]?.label}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <select value={halaqahId} onChange={e => setHalaqahId(e.target.value)}
                    data-testid="setoran-halaqah-select"
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                    <option value="">-- Halaqah --</option>
                    {halaqah.map(h => <option key={h.id} value={h.id}>{h.nama}</option>)}
                  </select>
                  <input type="date" required value={tanggal} onChange={e => setTanggal(e.target.value)}
                    data-testid="setoran-tanggal-input"
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
                {santri.length === 0 && (
                  <p data-testid="setoran-no-santri-hint" className="text-[10px] text-amber-600 mt-2 bg-amber-50 rounded-lg px-2.5 py-1.5">
                    Belum ada santri di halaqah Anda. Minta admin menghubungkan halaqah dengan akun guru Anda.
                  </p>
                )}
              </div>

              {/* ── Data Hafalan ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
                <div className="px-4 pt-4 pb-2">
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Data Hafalan</h3>
                </div>

                {/* Iqro – hanya kelas D */}
                {isIdad && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-bold text-emerald-800">Setoran Binnazhar Iqro</span>
                      <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">I'dad</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase">Jilid</label>
                        <select value={iqroJilid} onChange={e => setIqroJilid(parseInt(e.target.value))}
                          data-testid="iqro-jilid-select"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                          {[1,2,3,4,5,6].map(j => <option key={j} value={j}>Jilid {j}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase">Halaman</label>
                        <input type="number" min={1} max={200} value={iqroHalaman}
                          onChange={e => setIqroHalaman(parseInt(e.target.value) || 1)}
                          data-testid="iqro-halaman-input"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Ziyadah – semua kelas termasuk D */}
                <div className="px-4 py-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-emerald-800">Setoran Ziyadah</span>
                    <Toggle active={ziyadahActive} onChange={setZiyadahActive} testId="toggle-ziyadah" />
                  </div>
                  <AnimatePresence>
                    {ziyadahActive && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                        <JuzHalamanInput data={ziyadah} onChange={setZiyadah} mode="ziyadah" testId="ziyadah" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Murajaah – semua kelas termasuk D */}
                <div className="px-4 py-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-emerald-800">Setoran Murajaah</span>
                    <Toggle active={murajaahActive} onChange={setMurajaahActive} testId="toggle-murajaah" />
                  </div>
                  <AnimatePresence>
                    {murajaahActive && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                        <JuzHalamanInput data={murajaah} onChange={setMurajaah} mode="range" testId="murajaah" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Binnazhar – hanya non-D */}
                {!isIdad && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-emerald-800">Setoran Bin Nadzar</span>
                      <Toggle active={binnazharActive} onChange={setBinnazharActive} testId="toggle-binnazhar" />
                    </div>
                    <AnimatePresence>
                      {binnazharActive && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                          <JuzHalamanInput data={binnazhar} onChange={setBinnazhar} mode="range" testId="binnazhar" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Tasmi' – hanya kelas C ke atas */}
                {isTasmiEligible && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-emerald-800">Tasmi'</span>
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">C ke atas</span>
                      </div>
                      <Toggle active={tasmiActive} onChange={setTasmiActive} testId="toggle-tasmi" />
                    </div>
                    <AnimatePresence>
                      {tasmiActive && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden pt-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase">Jumlah Juz</label>
                              <input type="number" min={1} max={30} value={jumlahJuz}
                                onChange={e => setJumlahJuz(parseInt(e.target.value) || 1)}
                                data-testid="tasmi-jumlah-juz"
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase">Status</label>
                              <div className="grid grid-cols-2 gap-1.5">
                                {['Lulus', 'Remedial'].map(s => (
                                  <button key={s} type="button"
                                    data-testid={`tasmi-status-${s.toLowerCase()}`}
                                    onClick={() => setTasmiStatus(s)}
                                    className={`py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${
                                      tasmiStatus === s
                                        ? s === 'Lulus' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-500 text-white border-red-500'
                                        : 'bg-white text-gray-400 border-gray-200'
                                    }`}>{s}</button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* ── Catatan Guru ── */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Catatan Guru</label>
                <textarea value={catatan} onChange={e => setCatatan(e.target.value)}
                  rows={3} placeholder="Catatan untuk santri..."
                  data-testid="catatan-guru-textarea"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
              </div>

              <button data-testid="submit-setoran-btn" type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-base shadow-lg shadow-emerald-200 transition-all disabled:opacity-60">
                {loading
                  ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <><Check size={18} /> Simpan Setoran</>}
              </button>
            </motion.form>

            {/* ═══ SETORAN TERBARU ═══ */}
            {recentSetoran.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Setoran Terbaru</p>
                <div className="space-y-2.5">
                  {recentSetoran.map(s => {
                    const santriName = santri.find(st => st.id === s.santri_id)?.name || 'Santri';
                    const jLabel = JENIS_LABEL_MAP[s.jenis] || s.jenis;
                    const jColor = JENIS_COLOR_MAP[s.jenis] || 'bg-gray-100 text-gray-600';
                    const detail = s.jml_halaman ? `${formatHalaman(s.jml_halaman)} hal · Juz ${s.juz || '-'}` : s.jenis === 'tasmi' ? `${s.jumlah_juz || 1} Juz` : s.iqro_jilid ? `Jilid ${s.iqro_jilid} Hal. ${s.iqro_halaman}` : '';
                    return (
                      <div key={s.id} className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800 truncate">{santriName}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${jColor}`}>{jLabel}</span>
                          </div>
                          <p className="text-[11px] text-gray-400 mt-0.5">{s.tanggal}{detail ? ` · ${detail}` : ''}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={() => setEditingSetoran(s)} data-testid={`edit-setoran-${s.id}`}
                            className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors">
                            <Pencil size={13} />
                          </button>
                          {confirmDeleteId === s.id ? (
                            <div className="flex gap-1">
                              <button onClick={() => handleDeleteSetoran(s.id)} data-testid={`confirm-delete-${s.id}`}
                                className="px-2 h-8 rounded-xl bg-red-500 text-white text-[10px] font-bold hover:bg-red-600">Ya</button>
                              <button onClick={() => setConfirmDeleteId(null)}
                                className="px-2 h-8 rounded-xl bg-gray-100 text-gray-500 text-[10px] font-bold">Batal</button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(s.id)} data-testid={`delete-setoran-${s.id}`}
                              className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-100 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ RIWAYAT HAFALAN ═══ */}
            <div data-testid="riwayat-hafalan-section">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Riwayat Hafalan</p>
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { key: 'ziyadah', label: 'Ziyadah' },
                  { key: 'murajaah', label: "Muroja'ah" },
                  { key: 'bin_nadzar', label: 'Binnadzar' },
                ].map(t => (
                  <button key={t.key}
                    data-testid={`riwayat-tab-${t.key}`}
                    onClick={() => setRiwayatTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      riwayatTab === t.key
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {santri.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">Belum ada santri di halaqah Anda</p>
              ) : (
                <div className="space-y-3">
                  {santri.map(s => {
                    const filtered = allSetoran.filter(st => {
                      if (st.santri_id !== s.id) return false;
                      if (riwayatTab === 'bin_nadzar') return st.jenis === 'bin_nadzar' || st.jenis === 'bin_nadhar';
                      return st.jenis === riwayatTab;
                    });
                    return (
                      <div key={s.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="flex items-center gap-2.5 px-4 py-3 bg-emerald-50 border-b border-emerald-100">
                          <div className="w-7 h-7 rounded-lg bg-emerald-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {s.foto
                              ? <img src={s.foto} alt={s.name} className="w-full h-full object-cover" />
                              : <span className="text-emerald-700 font-bold text-xs">{s.name?.charAt(0)}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span data-testid={`riwayat-santri-${s.id}`} className="text-sm font-semibold text-emerald-900 truncate">{s.name}</span>
                              {s.kelas && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${KELAS_CONFIG[s.kelas]?.color}`}>
                                  {s.kelas}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-[10px] font-semibold text-emerald-600 flex-shrink-0">{filtered.length} catatan</span>
                        </div>
                        {filtered.length === 0 ? (
                          <p className="text-center text-[11px] text-gray-400 py-3">Belum ada riwayat</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[300px]">
                              <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                  <th className="px-3 py-1.5 text-[9px] font-bold text-gray-500 text-left">Tanggal</th>
                                  <th className="px-3 py-1.5 text-[9px] font-bold text-gray-500 text-center">Juz</th>
                                  <th className="px-3 py-1.5 text-[9px] font-bold text-gray-500 text-center">Rentang Hal</th>
                                  <th className="px-3 py-1.5 text-[9px] font-bold text-gray-500 text-center">Jml</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filtered.map((st, idx) => (
                                  <tr key={st.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                    <td className="px-3 py-1.5 text-[10px] text-gray-600 whitespace-nowrap">{st.tanggal || '-'}</td>
                                    <td className="px-3 py-1.5 text-[10px] text-center text-gray-700 font-medium">{st.juz || '-'}</td>
                                    <td className="px-3 py-1.5 text-[10px] text-center text-gray-500">
                                      {st.dari_halaman != null && st.sampai_halaman != null
                                        ? `${st.dari_halaman}–${st.sampai_halaman}`
                                        : '-'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[10px] text-center font-semibold text-emerald-700">
                                      {st.jml_halaman != null ? formatHalaman(st.jml_halaman) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>)}

          {tab === 'absensi' && (
            <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onSubmit={submitAbsensi} data-testid="absensi-form" className="space-y-4">
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Data Absensi</h3>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Santri *</label>
                  <select required value={formA.santri_id} onChange={e => setFormA(f => ({...f, santri_id: e.target.value}))}
                    data-testid="absensi-santri-select"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                    <option value="">-- Pilih Santri --</option>
                    {santri.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Halaqah</label>
                    <select value={formA.halaqah_id} onChange={e => setFormA(f => ({...f, halaqah_id: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      <option value="">-- Pilih --</option>
                      {halaqah.map(h => <option key={h.id} value={h.id}>{h.nama}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tanggal</label>
                    <input type="date" required value={formA.tanggal} onChange={e => setFormA(f => ({...f, tanggal: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                </div>
              </div>

              {/* Dua sesi */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Kehadiran Per Sesi</h3>
                  <span className="text-[10px] text-gray-400">Isi salah satu atau keduanya</span>
                </div>
                <SesiBlock title="Sesi 1 · Sore" value={sesiSore} onChange={setSesiSore} testIdPrefix="sesi-sore" />
                <SesiBlock title="Sesi 2 · Malam" value={sesiMalam} onChange={setSesiMalam} testIdPrefix="sesi-malam" />
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Catatan</label>
                  <input value={formA.catatan} onChange={e => setFormA(f => ({...f, catatan: e.target.value}))}
                    placeholder="Opsional..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                </div>
              </div>

              <button data-testid="submit-absensi-btn" type="submit" disabled={loadingA}
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-emerald-200 transition-all disabled:opacity-60">
                {loadingA ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Check size={18} /> Simpan Absensi</>}
              </button>
            </motion.form>
          )}
        </div>
      </div>

      {/* Edit Setoran Modal */}
      {editingSetoran && (
        <EditSetoranModal
          setoran={editingSetoran}
          onClose={() => setEditingSetoran(null)}
          onSaved={() => { setEditingSetoran(null); loadRecentSetoran(); }}
        />
      )}
    </Layout>
  );
}
